



















































































































































import {Component, Mixins, Prop, Watch} from 'vue-property-decorator';
import Card from '@/components/UI/Card.vue';
import Http from '@/mixins/http';
import Toast from '@/mixins/toast';
import LocalData from '@/mixins/local-data';
import {EventBus} from '@/mixins/event-bus';
import {SelectMarket} from '@/interfaces/global-data';
import {FilesPageParams, FileType, ServerFile, UserShortInfo} from '@/interfaces/server-data';
import {downloadBlobAsFile} from '@/utils/utils';

const POLL_INTERVAL_MS = 5000;
const STATUS_DOWNLOADING = 'DOWNLOADING';
const STATUS_ERROR = 'ERROR';

interface ITableFilterValues {
  fileNameFilterText: string | null;
  fileUploadDateFilterValue: string | null;
  userNameFilterValue: string | null;
}

@Component({
  components: {
    Card,
  },
})
export default class ProductLineDirectoryFiles extends Mixins(Http, Toast, LocalData) {
  @Prop({required: true}) readonly market!: SelectMarket | null;
  @Prop({default: 0}) readonly refreshTrigger!: number;

  table: ServerFile[] = [];
  pagination: any = {
    current: 1,
    pageSize: 20,
    total: 0,
  };
  isTableLoading: boolean = false;
  tableFilterOptions: any = {};
  tableFilterValues: ITableFilterValues = {
    fileNameFilterText: null,
    fileUploadDateFilterValue: null,
    userNameFilterValue: null,
  };

  pollHandle: number | null = null;
  loadRequestId: number = 0;

  get tableColumns(): any[] {
    return [
      {
        dataIndex: 'id',
        title: this.$t('masterData.productLine.filesTable.number') as string,
        width: 80,
      },
      {
        dataIndex: 'filePath',
        title: this.$t('masterData.productLine.filesTable.fileName') as string,
        filterModelKey: 'fileNameFilterText',
        scopedSlots: {
          filterDropdown: 'filterTextInput',
          filterIcon: 'filterIcon',
        },
        ellipsis: true,
        width: 320,
      },
      {
        dataIndex: 'status_warnings',
        title: this.$t('masterData.productLine.filesTable.status') as string,
        scopedSlots: {customRender: 'status'},
        ellipsis: true,
      },
      {
        dataIndex: 'fileUploadDate',
        title: this.$t('masterData.productLine.filesTable.uploadDate') as string,
        filterModelKey: 'fileUploadDateFilterValue',
        scopedSlots: {
          filterDropdown: 'filterDatepicker',
          filterIcon: 'filterIcon',
        },
        ellipsis: true,
        width: 200,
      },
      {
        dataIndex: 'username',
        title: this.$t('masterData.productLine.filesTable.user') as string,
        filterModelKey: 'userNameFilterValue',
        filterOptionsKey: 'usersFilterOptions',
        scopedSlots: {
          filterDropdown: 'filterRadioButtons',
          filterIcon: 'filterIcon',
        },
        ellipsis: true,
        width: 200,
      },
      {
        dataIndex: 'actions',
        title: this.$t('masterData.productLine.filesTable.actions') as string,
        scopedSlots: {customRender: 'actions'},
        width: 120,
      },
    ];
  }

  @Watch('market')
  onMarketChange() {
    this.resetFiltersAndReload();
  }

  @Watch('refreshTrigger')
  onRefreshTrigger() {
    this.refreshTable();
    this.ensurePolling();
  }

  resetFiltersAndReload() {
    this.loadRequestId++;
    this.tableFilterValues = {
      fileNameFilterText: null,
      fileUploadDateFilterValue: null,
      userNameFilterValue: null,
    };
    this.pagination = {...this.pagination, current: 1, total: 0};
    this.table = [];
    this.stopPolling();
    this.loadUsersFilterOptions();
    if (this.market) {
      this.refreshTable();
    }
  }

  applyFilters() {
    this.pagination = {...this.pagination, current: 1};
    this.refreshTable();
  }

  refreshTable() {
    if (!this.market) {
      this.table = [];
      this.pagination = {...this.pagination, total: 0};
      return;
    }
    this.loadFiles(this.pagination.current, this.pagination.pageSize);
  }

  loadFiles(currentPage: number, perPage: number) {
    if (!this.market) return;

    const pageParams: FilesPageParams = {
      type: FileType.PRODUCT_LINE_DIRECTORY,
      tradingId: this.market.id,
      size: perPage,
      page: currentPage - 1,
    };

    if (this.tableFilterValues.fileNameFilterText) {
      pageParams.fileNameFilterText = this.tableFilterValues.fileNameFilterText;
    }
    if (this.tableFilterValues.fileUploadDateFilterValue) {
      pageParams.fileUploadDateFilterValue = this.tableFilterValues.fileUploadDateFilterValue;
    }
    if (this.tableFilterValues.userNameFilterValue) {
      pageParams.userNameFilterValue = this.tableFilterValues.userNameFilterValue;
    }

    const requestId = ++this.loadRequestId;
    this.isTableLoading = true;
    this.httpGetFileList(pageParams)
      .then((response) => {
        if (requestId !== this.loadRequestId) return;
        const page = response.data;
        this.table = (page && page.content) || [];
        this.pagination = {
          ...this.pagination,
          total: (page && page.totalElements) || 0,
        };
      })
      .catch(() => {
        if (requestId !== this.loadRequestId) return;
        this.showError(this.$t('shared.messages.errorLoadFileList') as string);
      })
      .finally(() => {
        if (requestId !== this.loadRequestId) return;
        this.isTableLoading = false;
        this.ensurePolling();
      });
  }

  onTableChange(pagination: any) {
    this.pagination = {
      ...this.pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    };
    this.refreshTable();
  }

  hasFileErrors(item: ServerFile): boolean {
    if (!item) return false;
    return item.status === STATUS_ERROR || !!(item.warnings && item.warnings.length > 0);
  }

  isInProgress(item: ServerFile): boolean {
    return !!item && item.status === STATUS_DOWNLOADING;
  }

  isError(item: ServerFile): boolean {
    return !!item && item.status === STATUS_ERROR;
  }

  formatStatusCell(item: ServerFile): string {
    let label: string;
    if (this.isError(item)) {
      label = this.$t('masterData.productLine.filesTable.statusError') as string;
    } else if (this.isInProgress(item)) {
      label = this.$t('masterData.productLine.filesTable.statusInProgress') as string;
    } else {
      label = this.$t('masterData.productLine.filesTable.statusDone') as string;
    }
    if (item && item.warnings && item.warnings.trim().length > 0) {
      return label + '. ' + item.warnings;
    }
    return label;
  }

  downloadErrorLog(fileId: number) {
    this.httpGetFileErrorsById(fileId)
      .then((response) => {
        const blob = response.data;
        if (!blob || blob.size === 0) {
          this.showInfo(this.$t('shared.messages.noErrors') as string);
          return;
        }
        downloadBlobAsFile(blob, this.$t('masterData.productLine.errorsDownloadFileName') as string);
      })
      .catch(() => {
        this.showError(this.$t('shared.messages.errorDownloadErrorLog') as string);
      });
  }

  ensurePolling() {
    const hasInProgress = this.table.some((row) => this.isInProgress(row));
    if (hasInProgress) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  startPolling() {
    if (this.pollHandle !== null) return;
    this.pollHandle = window.setInterval(() => {
      if (!this.market) {
        this.stopPolling();
        return;
      }
      this.loadFiles(this.pagination.current, this.pagination.pageSize);
    }, POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.pollHandle !== null) {
      window.clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  loadUsersFilterOptions() {
    if (!this.market) {
      this.$set(this.tableFilterOptions, 'usersFilterOptions', []);
      return;
    }
    this.httpGetFileUploadUsers(FileType.PRODUCT_LINE_DIRECTORY, this.market.id)
      .then((response) => {
        this.$set(
          this.tableFilterOptions,
          'usersFilterOptions',
          (response.data || []).map((u: UserShortInfo) => ({value: u.username, label: u.fio || u.username}))
        );
      })
      .catch(() => {});
  }

  onFileEventReceived(payload: ServerFile) {
    if (!payload) return;
    if (payload.type !== FileType.PRODUCT_LINE_DIRECTORY) return;
    if (!this.market || !payload.tradingNetworks || payload.tradingNetworks.id !== this.market.id) return;
    this.refreshTable();
  }

  mounted() {
    this.loadUsersFilterOptions();
    if (this.market) {
      this.refreshTable();
    }
    EventBus.$on('file', this.onFileEventReceived);
  }

  beforeDestroy() {
    EventBus.$off('file', this.onFileEventReceived);
    this.stopPolling();
  }
}
